// app/(app)/inventory/products/[id].tsx
import { Button, Input } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { Unit } from "@/services/unitsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  deleteProduct,
  fetchProductById,
  resetSelected,
  updateProduct,
} from "@/store/slices/productSlice";
import { fetchUnits } from "@/store/slices/unitSlice";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function EditProductScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedProduct, loading, updating, error } = useAppSelector(
    (state) => state.products,
  );

  const gstOptions = [
    { label: "0%", value: "0" },
    { label: "5%", value: "5" },
    { label: "12%", value: "12" },
    { label: "18%", value: "18" },
    { label: "28%", value: "28" },
  ];

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [price, setPrice] = useState("");
  const [packQuantity, setPackQuantity] = useState("");
  const [gstPercent, setGstPercent] = useState("");
  const [unitId, setUnitId] = useState<number | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [errors, setErrors] = useState({
    name: "",
    sku: "",
    hsnCode: "",
    price: "",
    packQuantity: "",
    gstPercent: "",
    unitId: "",
  });

  useEffect(() => {
    const loadUnits = async () => {
      const result = await dispatch(
        fetchUnits({ page: 1, limit: 100, search: "" }),
      );
      if (fetchUnits.fulfilled.match(result)) {
        setUnits(result.payload.units);
      }
    };
    loadUnits();
    dispatch(fetchProductById(Number(id)));
    return () => {
      dispatch(resetSelected());
    };
  }, [id]);

  useEffect(() => {
    if (selectedProduct) {
      setName(selectedProduct.name);
      setSku(selectedProduct.sku);
      setHsnCode(selectedProduct.hsnCode);
      setPrice(selectedProduct.price.toString());
      setPackQuantity(selectedProduct.packQuantity.toString());
      setGstPercent(selectedProduct.gstPercent);
      setUnitId(selectedProduct.unitId);
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (error) {
      Alert.alert("Error", error);
      dispatch(clearError());
    }
  }, [error]);

  const validate = () => {
    let valid = true;
    const newErrors = {
      name: "",
      sku: "",
      hsnCode: "",
      price: "",
      packQuantity: "",
      gstPercent: "",
      unitId: "",
    };
    if (!name.trim()) newErrors.name = "Required";
    if (!sku.trim()) newErrors.sku = "Required";
    if (!hsnCode.trim()) newErrors.hsnCode = "Required";
    const priceNum = parseFloat(price);
    if (!price) newErrors.price = "Required";
    else if (isNaN(priceNum) || priceNum <= 0) newErrors.price = "Must be > 0";
    const pack = parseInt(packQuantity);
    if (!packQuantity) newErrors.packQuantity = "Required";
    else if (isNaN(pack) || pack < 1) newErrors.packQuantity = "Must be ≥ 1";
    const gst = parseFloat(gstPercent);
    if (!gstPercent) newErrors.gstPercent = "Required";
    else if (isNaN(gst) || gst < 0 || gst > 100)
      newErrors.gstPercent = "0-100 only";
    if (!unitId) newErrors.unitId = "Select unit";
    setErrors(newErrors);
    return !Object.values(newErrors).some((e) => e);
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    const result = await dispatch(
      updateProduct({
        id: Number(id),
        data: {
          name,
          sku,
          hsnCode,
          price: parseFloat(price),
          packQuantity: parseInt(packQuantity),
          unitId: unitId || 0,
          gstPercent,
        },
      }),
    );
    if (updateProduct.fulfilled.match(result))
      Alert.alert("Success", "Product updated", [
        { text: "OK", onPress: () => router.back() },
      ]);
  };

  const handleDelete = () => {
    Alert.alert("Delete", `Delete ${name}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          const result = await dispatch(deleteProduct(Number(id)));
          if (deleteProduct.fulfilled.match(result)) router.back();
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    loadingContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    idText: { fontSize: 14, color: colors.textLight, marginBottom: spacing.lg },
    inputContainer: { marginBottom: spacing.lg },
    deleteButton: { marginTop: spacing.lg },
  });

  if (loading)
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      <Text style={styles.title}>Edit Product</Text>
      <Text style={styles.idText}>ID: {id}</Text>
      <Input
        label="Product Name"
        value={name}
        onChangeText={setName}
        error={errors.name}
        containerStyle={styles.inputContainer}
      />
      <Input
        label="SKU"
        value={sku}
        onChangeText={setSku}
        error={errors.sku}
        containerStyle={styles.inputContainer}
      />
      <Input
        label="HSN Code"
        value={hsnCode}
        onChangeText={setHsnCode}
        error={errors.hsnCode}
        containerStyle={styles.inputContainer}
      />
      <Input
        label="Price (₹)"
        value={price}
        onChangeText={setPrice}
        keyboardType="numeric"
        error={errors.price}
        containerStyle={styles.inputContainer}
      />
      <Input
        label="Pack Quantity"
        value={packQuantity}
        onChangeText={setPackQuantity}
        keyboardType="numeric"
        error={errors.packQuantity}
        containerStyle={styles.inputContainer}
      />
      <SearchableSelect
        label="GST %"
        placeholder="Select GST rate"
        options={gstOptions}
        value={gstPercent}
        onValueChange={setGstPercent}
        error={errors.gstPercent}
        containerStyle={styles.inputContainer}
      />
      <SearchableSelect
        label="Unit"
        options={units.map((u) => ({
          label: `${u.name} (${u.symbol})`,
          value: u.id.toString(),
        }))}
        value={unitId?.toString()}
        onValueChange={(val) => setUnitId(parseInt(val))}
        error={errors.unitId}
        containerStyle={styles.inputContainer}
      />
      <Button
        title="Update Product"
        onPress={handleUpdate}
        loading={updating}
      />
      <Button
        title="Delete Product"
        onPress={handleDelete}
        variant="danger"
        containerStyle={styles.deleteButton}
      />
    </ScrollView>
  );
}
